import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetch, http } from "@/lib/api";
import { PageHeader, Loading, ScoreChip, ScoreBar, StatCard, SectionTitle, Copyable } from "@/components/common";
import AIResearch from "@/components/AIResearch";
import WalletRelationshipGraph from "@/components/WalletRelationshipGraph";
import { fmtUsd, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { UserSearch, ArrowLeft, Radio, GitBranch, AlertTriangle } from "lucide-react";

export default function WalletProfile() {
  const { address } = useParams();
  const nav = useNavigate();
  const { data, loading } = useFetch(`/wallets/${address}`, [address]);
  if (loading || !data) return <Loading />;
  const w = data.data;

  const track = async () => {
    await http.post("/tracked-wallets", { kind: "wallet", ref_id: w.address, label: w.short, sub: w.category });
    toast.success("Wallet added to tracker");
  };

  const milestones = [
    ["< $50K", w.milestones.below_50k],
    ["< $100K", w.milestones.below_100k],
    ["< $250K", w.milestones.below_250k],
    ["< $500K", w.milestones.below_500k],
    ["< $1M", w.milestones.below_1m],
  ];

  return (
    <div className="fade-in">
      <button onClick={() => nav(-1)} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-400">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <PageHeader
        icon={UserSearch}
        title={w.short}
        subtitle={<Copyable value={w.address} display={w.address} />}
        actions={
          <button data-testid="wallet-track-btn" onClick={track} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-600/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/20">
            <Radio className="h-3.5 w-3.5" /> Track
          </button>
        }
      />

      <div className="mb-5 flex flex-wrap gap-2">
        {w.labels.map((l) => (
          <span key={l} className="rounded-lg border border-slate-700 bg-slate-800/50 px-2.5 py-1 text-xs text-slate-300">{l}</span>
        ))}
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Smart Score" value={w.smart_score} sub={`conf ${w.smart_confidence}%`} />
        <StatCard label="SOL Balance" value={`${fmtNum(w.sol_balance)}`} />
        <StatCard label="Portfolio" value={fmtUsd(w.portfolio_value)} />
        <StatCard label="Analyzable Trades" value={w.analyzable_trades} sub={`${w.history_period}`} />
        <StatCard label="Early Entry" value={`${w.early_entry_rate}%`} tone="pos" />
        <StatCard label="Realized PnL" value={fmtUsd(w.realized_pnl)} tone={w.realized_pnl >= 0 ? "pos" : "neg"} />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="pnut-card p-5">
          <SectionTitle>Smart Wallet Score Breakdown</SectionTitle>
          <div className="mb-3 text-center">
            <div className="font-display text-4xl font-bold text-amber-400">{w.smart_score}</div>
            <div className="font-mono text-[11px] text-slate-500">based on {w.analyzable_trades} analyzable trades</div>
          </div>
          <div className="space-y-2.5">
            {Object.entries(w.smart_breakdown).map(([k, v]) => (
              <ScoreBar key={k} label={k.replace(/_/g, " ")} value={v} />
            ))}
          </div>
        </div>

        <div className="pnut-card p-5">
          <SectionTitle>Early Entry Profile</SectionTitle>
          <p className="mb-3 text-[11px] text-slate-500">Entries made below each market cap milestone. Sample &amp; period always shown. Not indicative of future performance.</p>
          <div className="space-y-2">
            {milestones.map(([l, v]) => (
              <div key={l} className="flex items-center justify-between rounded-md border border-slate-800 bg-void/50 px-3 py-2">
                <span className="font-mono text-xs text-slate-400">{l}</span>
                <span className="font-mono text-sm font-semibold text-slate-100">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Mini label="$500K" v={w.reached_500k} /><Mini label="$1M" v={w.reached_1m} /><Mini label="$5M" v={w.reached_5m} />
          </div>
        </div>

        <div className="pnut-card p-5">
          <SectionTitle right={<GitBranch className="h-4 w-4 text-slate-500" />}>Funding & Relationships</SectionTitle>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Funding source</span><span className="font-mono text-slate-200">{w.funding_source}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Narrative spec</span><span className="text-amber-400">{w.narrative_spec}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Wallet age</span><span className="text-slate-200">{w.age_days}d</span></div>
          </div>
          <div className="mt-3">
            <div className="mb-1.5 font-mono text-[10px] uppercase text-slate-500">Related wallets</div>
            <div className="flex flex-wrap gap-1.5">
              {w.related_wallets.length ? w.related_wallets.map((r, i) => (
                <span key={i} className="rounded border border-slate-800 bg-void/50 px-2 py-0.5 font-mono text-[10px] text-slate-400">{r}</span>
              )) : <span className="text-xs text-slate-600">None strongly supported</span>}
            </div>
          </div>
          <div className="mt-3 rounded-md border border-rose-900/40 bg-rose-950/10 p-2.5">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase text-rose-400"><AlertTriangle className="h-3 w-3" /> Risk flags</div>
            {w.risk_flags.map((f, i) => (<div key={i} className="text-[11px] text-slate-400">• {f}</div>))}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <WalletRelationshipGraph address={w.address} />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <TokenList title="What Is This Wallet Buying? (Recent Buys)" rows={w.recent_buys} nav={nav} tone="pos" />
        <TokenList title="Recent Exits" rows={w.recent_sells} nav={nav} tone="neg" />
      </div>

      <div className="mt-5">
        <AIResearch kind="wallet" refId={w.address} presets={["What kind of wallet is this?", "What tokens did it enter early?", "What narratives does it specialize in?"]} />
      </div>
    </div>
  );
}

function TokenList({ title, rows, nav, tone }) {
  return (
    <div className="pnut-card overflow-hidden">
      <div className="border-b border-slate-800 px-4 py-2.5 font-display text-sm font-semibold text-slate-200">{title}</div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">No records.</p>
      ) : (
        <div className="max-h-72 overflow-auto">
          <table className="pnut-table w-full">
            <thead><tr><th>Token</th><th>Entry MC</th><th>Amount</th><th>Narrative</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} onClick={() => nav(`/token/${r.token_id}`)}>
                  <td className="font-semibold text-slate-200">${r.symbol}</td>
                  <td className={`font-mono ${tone === "pos" ? "num-pos" : "num-neg"}`}>{fmtUsd(r.mc)}</td>
                  <td className="font-mono text-slate-400">{r.amount_sol} SOL</td>
                  <td className="text-slate-500">{r.narrative}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Mini({ label, v }) {
  return (
    <div className="rounded-md border border-slate-800 bg-void/50 py-2">
      <div className="font-display text-lg font-bold text-emerald-400">{v}</div>
      <div className="font-mono text-[10px] text-slate-500">{label}</div>
    </div>
  );
}
